import { blocked, burst, damagePlayer, destroy, distance, lineClear, moveActor, obstructed, propRadius, random, raiseAlarm, blastCells, type Enemy, type EnemyKind, type State } from './game.ts';

export const ENEMY_STATS:Record<EnemyKind,{hp:number;speed:number;radius:number;name:string}>={
  guard:{hp:6,speed:44,radius:9,name:'Guardia'},
  archer:{hp:5,speed:41,radius:9,name:'Arciere'},
  thrower:{hp:7,speed:46,radius:9,name:'Lanciatore'},
  brute:{hp:18,speed:28,radius:14,name:'Spaccabotti'},
  rat:{hp:2,speed:69,radius:6,name:'Ratto delle dispense'},
  bat:{hp:2,speed:53,radius:6,name:'Pipistrello di miniera'},
};
export function makeEnemy(s:State,kind:EnemyKind,x:number,y:number):Enemy {
  const hp=ENEMY_STATS[kind].hp+(s.room>2&&kind!=='bat'&&kind!=='rat'?1:0);
  return {id:s.nextId++,kind,x,y,hp,maxHp:hp,hit:0,attack:.5+random(s),dx:0,dy:1,moving:false,walk:0,mode:'patrol',timer:0,duration:0,aimX:x,aimY:y,axeId:null,path:[],repath:0,alert:0,homeX:x,homeY:y,lastX:x,lastY:y};
}

// A small breadth-first search is sufficient for a 20 × 11 room. Recompute at most
// three times per second per actor, so moved crates become obstacles immediately.
export function findPath(s:State,start:{x:number;y:number},goal:{x:number;y:number},radius=9,flying=false):number[]{
  const from=Math.floor(start.y/32)*20+Math.floor(start.x/32);
  const parents=new Int16Array(220).fill(-1),cost=new Int16Array(220).fill(32767);
  const queue=[from];parents[from]=from;cost[from]=0;
  let best=from,bestScore=Infinity;
  const canWalk=(i:number)=>{const x=(i%20)*32+16,y=Math.floor(i/20)*32+16;return flying?!blocked(s,x,y,radius):!obstructed(s,x,y,radius);};
  for(let head=0;head<queue.length;head++){
    const cell=queue[head]!,x=(cell%20)*32+16,y=Math.floor(cell/20)*32+16;
    const d=Math.hypot(x-goal.x,y-goal.y),score=d+cost[cell]!*1.5;
    if(score<bestScore){bestScore=score;best=cell;}
    for(const step of [-20,20,-1,1]){
      const next=cell+step;if(next<0||next>=220||Math.abs(next%20-cell%20)>1||parents[next]!==-1||!canWalk(next))continue;
      parents[next]=cell;cost[next]=cost[cell]!+1;queue.push(next);
    }
  }
  const path:number[]=[];for(let at=best;at!==from&&at>=0;at=parents[at]!)path.unshift(at);
  return path;
}
function steer(s:State,e:Enemy,goal:{x:number;y:number},speed:number,dt:number){
  const config=ENEMY_STATS[e.kind],flying=e.kind==='bat';let point=goal;
  if(!lineClear(s,e,goal,config.radius,flying)){
    if(e.repath<=0){e.path=findPath(s,e,goal,config.radius,flying);e.repath=.3+(e.id%4)*.04;}
    while(e.path.length){const cell=e.path[0]!,waypoint={x:cell%20*32+16,y:Math.floor(cell/20)*32+16};if(distance(e,waypoint)<4)e.path.shift();else{point=waypoint;break;}}
    if(!e.path.length)return;
  }else e.path=[];
  const d=distance(e,point);if(d<2)return;
  const dx=(point.x-e.x)/d,dy=(point.y-e.y)/d;
  let sx=0,sy=0;
  for(const other of s.enemies)if(other!==e&&other.hp>0){const separation=distance(e,other),r=config.radius+ENEMY_STATS[other.kind].radius+3;if(separation>0&&separation<r){sx+=(e.x-other.x)/separation*.6;sy+=(e.y-other.y)/separation*.6;}}
  const old={x:e.x,y:e.y};moveActor(s,e,(dx+sx)*speed*dt,(dy+sy)*speed*dt,config.radius,flying);
  const traveled=distance(old,e);e.moving=traveled>.01;e.walk+=traveled/(e.kind==='brute'?12:e.kind==='rat'?6:9);
  if(e.moving){e.dx=dx;e.dy=dy;}else e.repath=0;
}
function begin(e:Enemy,mode:Enemy['mode'],duration:number,point:{x:number;y:number}){
  e.mode=mode;e.timer=duration;e.duration=duration;e.aimX=point.x;e.aimY=point.y;e.moving=false;
  const d=distance(e,point);if(d>.01){e.dx=(point.x-e.x)/d;e.dy=(point.y-e.y)/d;}
}
function vantage(s:State,e:Enemy,target:{x:number;y:number},range:number):{x:number;y:number}{
  let best={x:e.x,y:e.y},score=Infinity;
  for(let i=0;i<12;i++){
    const angle=i*Math.PI/6,point={x:target.x+Math.cos(angle)*range,y:target.y+Math.sin(angle)*range};
    if(obstructed(s,point.x,point.y,10)||!lineClear(s,point,target,3))continue;
    // Prefer the near side of the target. A blocked firing lane makes archers relocate.
    const value=distance(e,point)+(lineClear(s,e,point,9)?0:60);
    if(value<score){best=point;score=value;}
  }
  return score<Infinity?best:target;
}
function fire(s:State,e:Enemy){
  const d=Math.hypot(e.aimX-e.x,e.aimY-e.y)||1,dx=(e.aimX-e.x)/d,dy=(e.aimY-e.y)/d;
  const kind=e.kind==='archer'?'arrow':'axe',id=s.nextId++;
  s.projectiles.push({id,kind,x:e.x+dx*15,y:e.y+dy*15,vx:dx*(kind==='arrow'?210:165),vy:dy*(kind==='arrow'?210:165),life:kind==='arrow'?2.8:2.4,owner:e.id,enemy:true,angle:Math.atan2(dy,dx)});
  if(kind==='axe')e.axeId=id;
  e.attack=kind==='arrow'?1.4:1.1;begin(e,'recover',.4,e);
}
function slam(s:State,e:Enemy){
  s.shake=6;burst(s,e.x,e.y,'#c5ab79',32);
  for(const p of s.players)if(distance(e,p)<51&&lineClear(s,e,p,2,true))damagePlayer(s,p,2);
  for(const prop of [...s.props])if(distance(e,prop)<52+propRadius(prop))destroy(s,prop);
  begin(e,'recover',1.15,e);e.attack=1.5;
}
export function updateEnemies(s:State,dt:number){
  const alive=s.players.filter(p=>p.hp>0);
  for(const e of s.enemies){
    if(e.hp<=0)continue;
    const stats=ENEMY_STATS[e.kind];
    e.hit=Math.max(0,e.hit-dt);e.attack=Math.max(0,e.attack-dt);e.repath-=dt;e.alert=Math.max(0,e.alert-dt);e.moving=false;
    const target=alive.reduce<typeof alive[number]|undefined>((best,p)=>!best||distance(p,e)<distance(best,e)?p:best,undefined);
    if(!target)continue;
    const d=distance(e,target);
    const visible=d<210&&lineClear(s,e,target,2);
    if(visible){e.alert=6;e.lastX=target.x;e.lastY=target.y;if(e.kind!=='bat'&&e.kind!=='rat'&&s.alarm===0)raiseAlarm(s,.2);}
    if(visible)for(const ally of s.enemies)if(ally!==e&&distance(ally,e)<115&&lineClear(s,e,ally,2,true)){ally.alert=Math.max(ally.alert,4);ally.lastX=e.lastX;ally.lastY=e.lastY;}
    if(e.hit>0)continue;
    if(e.mode==='charge'){
      e.timer-=dt;
      const old={x:e.x,y:e.y};
      for(const prop of [...s.props])if(distance(prop,e)<stats.radius+propRadius(prop)+10)destroy(s,prop);
      moveActor(s,e,e.dx*165*dt,e.dy*165*dt,stats.radius);e.walk+=distance(e,old)/10;e.moving=true;
      if(e.timer<=0||distance(old,e)<.2||alive.some(p=>distance(e,p)<24))slam(s,e);
      continue;
    }
    if(e.mode==='aim'||e.mode==='windup'){
      e.timer-=dt;
      if(e.timer<=0){
        if(e.mode==='aim'){
          // Check the lane again: never shoot through a newly pushed crate or an ally.
          const point={x:e.aimX,y:e.aimY};
          const allyInLane=s.enemies.some(other=>other!==e&&distance(other,e)<distance(e,point)&&distanceToSegment(other,e,point)<14);
          if(lineClear(s,e,point,3)&&!allyInLane)fire(s,e);else{begin(e,'recover',.25,e);e.repath=0;}
        }else if(e.kind==='brute'){begin(e,'charge',.6,{x:e.aimX,y:e.aimY});}
        else{
          for(const p of alive)if(distance(e,p)<(e.kind==='guard'?33:26)&&lineClear(s,e,p,2,true)){
            const len=distance(e,p)||1;if(((p.x-e.x)*e.dx+(p.y-e.y)*e.dy)/len>-.1)damagePlayer(s,p,1);
          }
          if(e.kind==='rat'||e.kind==='bat')moveActor(s,e,e.dx*13,e.dy*13,stats.radius,e.kind==='bat');
          begin(e,'recover',e.kind==='guard'?.5:.45,e);e.attack=.75;
        }
      }
      continue;
    }
    if(e.mode==='recover'){e.timer-=dt;if(e.timer>0)continue;e.mode='chase';}
    // A thrown axe remains a physical projectile. The thrower cannot create another
    // until it reaches and removes its own lodged weapon (including moving props).
    if(e.kind==='thrower'&&e.axeId!==null){
      const axe=s.projectiles.find(b=>b.id===e.axeId);
      if(!axe){e.axeId=null;e.attack=.8;}
      else{
        e.mode='retrieve';
        if(axe.stuck){
          const attached=s.props.find(p=>p.id===axe.stuckOn);
          const reach=attached?propRadius(attached)+17:27;
          if(distance(e,axe)<reach&&lineClear(s,e,axe,2,true)){
            s.projectiles=s.projectiles.filter(b=>b!==axe);e.axeId=null;begin(e,'recover',.45,e);e.attack=.7;burst(s,e.x,e.y-10,'#e4c78a',5);
          }else steer(s,e,axe,stats.speed*1.15,dt);
        }else steer(s,e,{x:axe.x-e.dx*35,y:axe.y-e.dy*35},stats.speed*.6,dt);
        continue;
      }
    }
    const bomb=s.projectiles.find(b=>b.kind==='bomb'&&b.life<1.3&&blastCells(s,b).includes(Math.floor(e.y/32)*20+Math.floor(e.x/32)));
    if(bomb&&e.kind!=='brute'){
      e.mode='evade';const len=distance(e,bomb)||1;
      const horizontal=Math.abs(e.x-bomb.x)>Math.abs(e.y-bomb.y);
      const options=horizontal?[{x:e.x,y:e.y+48},{x:e.x,y:e.y-48}]:[{x:e.x+48,y:e.y},{x:e.x-48,y:e.y}];
      const escape=options.find(p=>!obstructed(s,p.x,p.y,stats.radius))||{x:e.x+(e.x-bomb.x)/len*100,y:e.y+(e.y-bomb.y)/len*100};
      steer(s,e,escape,stats.speed*1.35,dt);continue;
    }
    if(!visible&&e.alert>0){
      e.mode='chase';const remembered={x:e.lastX,y:e.lastY};
      if(distance(e,remembered)>12)steer(s,e,remembered,stats.speed*.8,dt);
      else{const phase=s.time+e.id;e.dx=Math.cos(phase);e.dy=Math.sin(phase);}
      continue;
    }
    if(e.alert<=0){
      e.mode='patrol';const phase=s.time*.4+e.id;
      steer(s,e,{x:e.homeX+Math.sin(phase)*30,y:e.homeY+Math.cos(phase)*20},stats.speed*.35,dt);continue;
    }
    if(e.kind==='archer'||e.kind==='thrower'){
      const range=e.kind==='archer'?155:130,minRange=e.kind==='archer'?100:75,maxRange=e.kind==='archer'?250:215;
      if(d>=minRange&&d<maxRange&&lineClear(s,e,target,3)&&e.attack<=0){
        // A short lead helps against running goblins, but the telegraph locks the shot.
        const lead=target.moving?.17:0;
        begin(e,'aim',e.kind==='archer'?.7:.6,{x:target.x+target.dx*97*lead,y:target.y+target.dy*97*lead});
      }else{
        e.mode='chase';if(d<minRange||!lineClear(s,e,target,3))steer(s,e,vantage(s,e,target,range),stats.speed,dt);
        else if(d>maxRange-30)steer(s,e,target,stats.speed,dt);
      }
      continue;
    }
    if(e.kind==='brute'&&d<115&&e.attack<=0&&lineClear(s,e,target,stats.radius,true)){begin(e,'windup',.95,target);continue;}
    if(e.kind!=='brute'&&d<(e.kind==='guard'?35:30)&&e.attack<=0&&lineClear(s,e,target,2)){begin(e,'windup',e.kind==='guard'?.38:.25,target);continue;}
    e.mode='chase';
    let goal={x:target.x,y:target.y};
    // Guards approach from different sides, rats make quick lateral approaches.
    if(d>45&&d<145&&e.kind!=='brute'){
      const angle=Math.atan2(e.y-target.y,e.x-target.x)+(e.id%2===0?.55:-.55);
      const flank={x:target.x+Math.cos(angle)*28,y:target.y+Math.sin(angle)*28};if(!obstructed(s,flank.x,flank.y,stats.radius))goal=flank;
    }
    steer(s,e,goal,stats.speed,dt);
    if(e.kind==='brute'&&!e.moving&&e.attack<=0){
      const blocking=s.props.find(o=>distance(o,e)<propRadius(o)+stats.radius+15);
      if(blocking){blocking.hp-=3;if(blocking.hp<=0)destroy(s,blocking);e.attack=.7;burst(s,e.x,e.y,'#b49a73',6);}
    }
  }
}
function distanceToSegment(p:{x:number;y:number},a:{x:number;y:number},b:{x:number;y:number}){
  const dx=b.x-a.x,dy=b.y-a.y,t=Math.max(0,Math.min(1,((p.x-a.x)*dx+(p.y-a.y)*dy)/(dx*dx+dy*dy||1)));
  return Math.hypot(p.x-a.x-t*dx,p.y-a.y-t*dy);
}
